<script>
import { getOptimizedImageUrl } from '@/composables/useOptimizedImage'

export default {
  inheritAttrs: false,
  props: {
    image_url: String,
    title: String,
    secondary: String,
    link: String,
    style_main: Boolean,
    style_about: Boolean,
    img_height: String,
    /** Alt/aria-label для доступности и SEO (по умолчанию — title или «Изображение») */
    alt: String,
    eager: Boolean,
    highPriority: Boolean,
  },
  data() {
    return {
      image_style: this.img_height ? `height:${this.img_height};` : '',
      isImageLoaded: false,
      preloadTimeoutId: null,
      responsiveWidths: [320, 480, 640, 960, 1280],
    }
  },
  computed: {
    optimizedImageSrc() {
      const source = (this.image_url || '').trim()
      if (!source) return ''
      return getOptimizedImageUrl(source, { width: 960, quality: 78, format: 'webp' }) || source
    },
    responsiveSrcSet() {
      const source = (this.image_url || '').trim()
      if (!source) return ''
      const variants = this.responsiveWidths
        .map(
          (width) =>
            `${getOptimizedImageUrl(source, { width, quality: 78, format: 'webp' })} ${width}w`,
        )
        .filter(Boolean)
      if (!variants.length) return ''
      // Если прокси не включен, srcset не даёт выгоды (все URL одинаковые).
      const uniqueUrls = new Set(variants.map((item) => item.split(' ')[0]))
      return uniqueUrls.size > 1 ? variants.join(', ') : ''
    },
    responsiveSizes() {
      // about-карточки обычно в 3-4 колонки, остальные — квадратные блоки направлений.
      if (this.style_about) return '(max-width: 1024px) 100vw, 33vw'
      return '(max-width: 768px) 100vw, 50vw'
    },
  },
  methods: {
    preloadImage() {
      this.isImageLoaded = false
      if (this.preloadTimeoutId) {
        clearTimeout(this.preloadTimeoutId)
      }
      this.preloadTimeoutId = setTimeout(() => {
        this.isImageLoaded = true
      }, 5000)
      if (!this.optimizedImageSrc) {
        this.isImageLoaded = true
        return
      }
      const img = new Image()
      img.onload = () => {
        if (this.preloadTimeoutId) {
          clearTimeout(this.preloadTimeoutId)
        }
        this.isImageLoaded = true
      }
      img.onerror = () => {
        if (this.preloadTimeoutId) {
          clearTimeout(this.preloadTimeoutId)
        }
        this.isImageLoaded = true
      }
      img.src = this.optimizedImageSrc
    },
  },
  created() {
    this.image_style = this.img_height ? `height:${this.img_height};` : ''
  },
  mounted() {
    this.preloadImage()
  },
  watch: {
    image_url() {
      this.preloadImage()
    },
    img_height() {
      this.image_style = this.img_height ? `height:${this.img_height};` : ''
    },
  },
  beforeUnmount() {
    if (this.preloadTimeoutId) {
      clearTimeout(this.preloadTimeoutId)
    }
  },
}
</script>

<template>
  <div
    class="col square square-image-root"
    :class="{ 'square-image-root--about': style_about }"
    v-bind="$attrs"
  >
    <router-link v-if="link" :to="link" class="link-hover square-link square-image-inner">
      <div
        class="image-square square"
        role="img"
        :aria-label="alt || title || 'Изображение'"
        :style="image_style"
      >
        <div v-if="!isImageLoaded" class="image-square__loader">
          <div class="skeleton-cover" :class="{ 'skeleton-cover--dark': style_about }"></div>
        </div>
        <img
          class="image-square__img"
          :src="optimizedImageSrc || image_url"
          :srcset="responsiveSrcSet || undefined"
          :sizes="responsiveSrcSet ? responsiveSizes : undefined"
          :alt="alt || title || 'Изображение'"
          :loading="eager ? 'eager' : 'lazy'"
          :fetchpriority="highPriority ? 'high' : 'auto'"
          decoding="async"
        />
        <div v-if="style_about" class="image-square__overlay"></div>
        <div v-if="style_main" class="image-square_text image-square_text_center">
          <p v-if="link !== 'production'" class="text-lg text-white image-square_textlg">
            {{ title }}
          </p>
          <span v-else class="text-lg text-white image-square_textlg">{{ title }}</span>
        </div>
        <div v-if="style_about" class="image-square_text image-square_text_left flex">
          <div class="flex card-content">
            <p class="text-title text-white">{{ title }}</p>
            <p class="text-desc text-white">{{ secondary }}</p>
          </div>
        </div>
      </div>
    </router-link>

    <div v-else class="card-mobile square-div square-image-inner">
      <div
        class="image-square square card-mobile"
        role="img"
        :aria-label="alt || title || 'Изображение'"
        :style="image_style"
      >
        <div v-if="!isImageLoaded" class="image-square__loader">
          <div class="skeleton-cover" :class="{ 'skeleton-cover--dark': style_about }"></div>
        </div>
        <img
          class="image-square__img"
          :src="optimizedImageSrc || image_url"
          :srcset="responsiveSrcSet || undefined"
          :sizes="responsiveSrcSet ? responsiveSizes : undefined"
          :alt="alt || title || 'Изображение'"
          :loading="eager ? 'eager' : 'lazy'"
          :fetchpriority="highPriority ? 'high' : 'auto'"
          decoding="async"
        />
        <div v-if="style_about" class="image-square__overlay"></div>
        <div v-if="style_main" class="image-square_text image-square_text_center">
          <p v-if="link !== 'production'" class="text-lg text-white">{{ title }}</p>
          <span v-else class="text-lg text-white image-square_textlg">{{ title }}</span>
        </div>
        <div v-if="style_about" class="image-square_text image-square_text_left flex">
          <div class="flex">
            <p class="text-title text-white">{{ title }}</p>
            <p class="text-desc text-white">{{ secondary }}</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.image-square {
  background-size: cover;
  background-position: center;
  background-repeat: space;
  width: 100%;
  overflow: hidden;
  position: relative;
}

.square {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  max-height: 80svh;
  height: auto;
}

.square-link,
.square-div {
  text-decoration: none;
}

.square-image-root {
  display: flex;
  justify-content: center;
  align-items: center;
}

.square-image-inner {
  width: 100%;
  height: 100%;
  display: block;
  text-decoration: none;
  color: inherit;
}

.image-square_text {
  display: flex;
  height: 100%;
  width: 100%;
  position: relative;
  z-index: 1;
}

.image-square_text.image-square_text_center {
  align-items: center;
  justify-content: center;
}

.image-square_text.image-square_text_left {
  align-items: flex-start;
  justify-content: flex-start;
}

.image-square_textlg {
  font-size: clamp(1.5rem, 5vw, 3.5rem);
  font-weight: 300;
  font-family: 'Montserrat', sans-serif;
  line-height: clamp(1.75rem, 5.5vw, 3.75rem);
  word-wrap: break-word;
  overflow-wrap: break-word;
  max-width: 100%;
  padding: 0 0.5rem;
  box-sizing: border-box;
}

.image-square_text.image-square_text_center span,
.image-square_text.image-square_text_center p {
  text-align: center !important;
  display: block !important;
  width: 100% !important;
  margin: 0 auto !important;
  text-align-last: center !important;
  word-wrap: break-word;
  overflow-wrap: break-word;
  max-width: 100%;
  box-sizing: border-box;
}

.image-square_text.image-square_text_left .flex {
  align-items: flex-start;
  justify-content: flex-end;
  width: calc(100% - 1.25rem);
}

.image-square__img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  transition: transform 0.5s ease;
  position: absolute;
  top: 0;
  left: 0;
  z-index: 0;
  opacity: 1;
  transition:
    transform 0.5s ease,
    opacity 0.25s ease;
}

.image-square__loader {
  position: absolute;
  inset: 0;
  z-index: 2;
  overflow: hidden;
}

.skeleton-cover {
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, #ececec 25%, #f5f5f5 50%, #ececec 75%);
  background-size: 200% 100%;
  animation: skeleton-shimmer 1.2s infinite linear;
}

.skeleton-cover--dark {
  background: linear-gradient(90deg, #1f1f1f 25%, #2b2b2b 50%, #1f1f1f 75%);
  background-size: 200% 100%;
}

@keyframes skeleton-shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

.image-square__overlay {
  position: absolute;
  inset: 0;
  z-index: 1;
  background: linear-gradient(0deg, rgba(0, 0, 0, 1) 0%, rgba(0, 0, 0, 0) 50%);
}

.image-square:hover .image-square__img {
  transform: scale(1.1);
}

.link-hover {
  display: inline-block;
  transition: box-shadow 0.3s ease;
}

.link-hover:hover {
  box-shadow: 0 0.25rem 0.938rem rgba(0, 0, 0, 0.2);
}

.card-content {
  padding: 1rem;
}

.text-title {
  font-family: Montserrat, Arial, sans-serif;
  font-size: 1.875rem;
  font-weight: 500;
  text-transform: uppercase;
}

.text-desc {
  font-family: Montserrat, Arial, sans-serif;
  font-size: 0.875rem;
  font-weight: 400;
  text-transform: uppercase;
  margin-top: 0.25rem;
}

@media (max-width: 87.5rem) {
  .text-title {
    font-size: 1.5rem;
  }

  .text-desc {
    font-size: 0.75rem;
  }
}

@media (max-width: 64rem) {
  .image-square_textlg {
    font-size: clamp(1.5rem, 4.5vw, 2.75rem);
    line-height: clamp(1.75rem, 5vw, 3rem);
  }
}

@media (max-width: 48rem) {
  .image-square_textlg {
    font-size: clamp(1.25rem, 4vw, 2.25rem);
    line-height: clamp(1.5rem, 4.5vw, 2.5rem);
  }

  .text-title {
    font-size: 1.25rem;
  }

  .text-desc {
    font-size: 0.625rem;
  }
}

@media (max-width: 36rem) {
  .image-square_textlg {
    font-size: clamp(1.5rem, 5vw, 2.5rem);
    line-height: clamp(1.75rem, 5.5vw, 2.75rem);
  }

  .square {
    aspect-ratio: auto;
    height: 25svh;
    min-height: auto;
  }

  .image-square_text.image-square_text_left.flex > .flex {
    flex-direction: column;
  }

  .text-title {
    font-size: 1.375rem;
  }

  .text-desc {
    font-size: 0.75rem;
  }

  .card-mobile {
    max-height: 20svh;
  }

  /* Участники (О нас): карточки чуть выше на мобилках */
  .square-image-root--about.square {
    height: 30svh;
    align-items: stretch;
  }

  .square-image-root--about .square-link.square-image-inner,
  .square-image-root--about .square-div.square-image-inner {
    display: block;
    height: 100%;
    min-height: 0;
    align-self: stretch;
  }

  .square-image-root--about .image-square.square {
    height: 100%;
    min-height: 0;
    max-height: none;
  }

  .square-image-root--about .card-mobile {
    max-height: none;
    height: 100%;
  }

  .square-image-root--about .image-square.square.card-mobile {
    height: 100%;
    max-height: none;
  }
}

@media (max-width: 22.5rem) {
  .square {
    height: 20svh;
    min-height: auto;
  }

  .square-image-root--about.square {
    height: 24svh;
    align-items: stretch;
  }
}

@media (max-width: 20rem) {
  .square {
    height: 18svh;
    min-height: auto;
  }

  .square-image-root--about.square {
    height: 22svh;
    align-items: stretch;
  }

  .image-square_textlg {
    font-size: clamp(1.25rem, 4.5vw, 2rem);
    line-height: clamp(1.5rem, 5vw, 2.25rem);
  }

  .text-title {
    font-size: 1.125rem;
  }

  .text-desc {
    font-size: 0.625rem;
  }
}

@media (min-width: 64rem) {
  .image-square_textlg {
    font-size: clamp(1.75rem, 3vw, 2.75rem);
    line-height: clamp(2rem, 3.5vw, 3rem);
  }

  .image-square_text.image-square_text_center span,
  .image-square_text.image-square_text_center p {
    text-align: center !important;
    display: block !important;
    width: 100% !important;
    margin: 0 auto !important;
    text-align-last: center !important;
  }
}

@media (min-width: 80rem) {
  .image-square_textlg {
    font-size: clamp(1.5rem, 2.5vw, 2.5rem);
    line-height: clamp(1.75rem, 3vw, 2.75rem);
  }
}

@media (min-width: 100rem) {
  .image-square_textlg {
    font-size: clamp(1.5rem, 2.5vw, 2.5rem);
    line-height: clamp(1.75rem, 3vw, 2.75rem);
  }
}

@media (min-width: 120rem) {
  .image-square_textlg {
    font-size: clamp(1rem, 1.75vw, 2rem);
    line-height: clamp(1.25rem, 2vw, 2.25rem);
  }
}
</style>
